import { createClient } from "@supabase/supabase-js";
import { getRuntimeEnvironment } from "../local/runtimeEnvironment";
import { resilientSupabaseStorage } from "./authStorage";
import {
  isSupabaseConfigured,
  supabaseAnonKey,
  supabaseUrl,
} from "./config";
import {
  buildApprovedWeRiderCompliance,
  buildInitialWeRiderCompliance,
  normalizeWeRiderCompliance,
} from "../local/weRiderCompliance";
import {
  clearLocalWeRiderIdentityDocuments,
  saveLocalWeRiderIdentityDocuments,
} from "../local/weRiderIdentityDocumentStore";

const RIDER_FUNCTION = "public-we-rider";
const RIDER_AUTH_STORAGE_KEY = "sb-mozzoapp-we-rider-auth-token";
const LOCAL_ACCOUNT_KEY = "mozzoapp:we-rider-local-account";
const LOCAL_SESSION_KEY = "mozzoapp:we-rider-local-session";
const LOCAL_JOBS_KEY = "mozzoapp:we-rider-local-jobs:v1";
const RIDER_BACKGROUND_DEVICE_KEY = "mozzoapp:we-rider:background-device:v1";
const RIDER_DOCUMENT_BUCKET = "we-rider-documents";
const RIDER_DOCUMENT_MAX_SIZE = 8 * 1024 * 1024;
const RIDER_LIVE_SELFIE_MAX_AGE_MS = 15 * 60 * 1000;
const RIDER_LIVENESS_METHOD = "mediapipe_active_challenge_v1";
const RIDER_MINIMUM_AGE = 18;
const RIDER_IDENTITY_CONSENT_VERSION = "2026-09-03";
const RIDER_TERMS_VERSION = "2026-09-03";
const RIDER_PRIVACY_VERSION = "2026-09-03";
const RIDER_DOCUMENT_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);
const RIDER_VEHICLE_TYPES = new Set([
  "bicycle",
  "scooter",
  "motorcycle",
  "car",
  "walking",
  "other",
]);
export const WE_RIDER_UPDATED_EVENT = "mozzoapp:we-rider-updated";

const BASE_IDENTITY_DOCUMENT_TYPES = Object.freeze([
  "dni_front",
  "dni_back",
  "selfie",
]);
const MOTORIZED_DOCUMENT_TYPES = Object.freeze([
  "driver_license_front",
  "driver_license_back",
  "vehicle_registration_front",
  "vehicle_registration_back",
]);

const LOCAL_TRACKING_STATUS = Object.freeze({
  waiting_restaurant: {
    label: "Esperando al restaurante",
    message: "El restaurante recibió tu pedido y debe confirmarlo.",
  },
  searching_driver: {
    label: "Buscando repartidor",
    message: "Estamos buscando un Rider disponible cerca del local.",
  },
  driver_assigned: {
    label: "Repartidor asignado",
    message: "Ya hay un Rider asignado a tu entrega.",
  },
  arriving_pickup: {
    label: "Yendo al restaurante",
    message: "Tu Rider está yendo al restaurante para retirar el pedido.",
  },
  ready_for_pickup: {
    label: "Listo para retirar",
    message: "El pedido está listo y el Rider puede retirarlo.",
  },
  arrived_pickup: {
    label: "Rider en el restaurante",
    message: "El Rider llegó al restaurante y está validando el retiro.",
  },
  picked_up: {
    label: "Pedido retirado",
    message: "El Rider ya retiró tu pedido del restaurante.",
  },
  in_transit: {
    label: "En camino",
    message: "Tu pedido está viajando hacia tu dirección.",
  },
  delivered: {
    label: "Entregado",
    message: "Tu pedido fue entregado.",
  },
  cancelled: {
    label: "Entrega cancelada",
    message: "La entrega fue cancelada. Comunícate con el restaurante si necesitas ayuda.",
  },
});

const DEMO_ACCOUNT = Object.freeze({
  userId: "local-we-rider-demo-user",
  id: "local-we-rider-demo",
  fullName: "Rider Prueba",
  email: "rider.prueba@we.local",
  phone: "3794123456",
  password: "Prueba1234",
  vehicleType: "motorcycle",
  vehicleReference: "Moto de prueba",
  status: "offline",
  compliance: buildApprovedWeRiderCompliance(),
});

const DEMO_JOBS = Object.freeze([
  {
    demo: true,
    id: "local-assignment-001",
    externalOrderId: "local-order-001",
    displayNumber: "DEL-17082026-A12F",
    status: "searching_driver",
    assigned: false,
    createdAt: new Date(Date.now() - 6 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 6 * 60 * 1000).toISOString(),
    estimatedReadyAt: new Date(Date.now() + 12 * 60 * 1000).toISOString(),
    pickup: {
      name: "Mozzo",
      address: "Catamarca 1378",
      city: "Corrientes",
      province: "Corrientes",
      latitude: -27.4698,
      longitude: -58.8332,
    },
    destination: {
      address: "",
      neighborhood: "Centro",
      city: "Corrientes",
      reference: "",
      latitude: null,
      longitude: null,
      approximate: true,
    },
    customer: { name: "Diego", phone: "" },
    payment: {
      method: "cash_on_delivery",
      status: "pending",
      subtotal: 11000,
      amount: 13500,
      deliveryFee: 2500,
      collectOnDelivery: true,
    },
    notes: "",
    distanceToPickupKm: 1.4,
    items: [
      { id: "item-001", name: "Milanesa Napolitana de Carne", quantity: 1, notes: "" },
      { id: "item-002", name: "Coca 330ml", quantity: 1, notes: "" },
    ],
    events: [{ status: "searching_driver", createdAt: new Date().toISOString() }],
  },
  {
    demo: true,
    id: "local-assignment-002",
    externalOrderId: "local-order-002",
    displayNumber: "DEL-17082026-B84C",
    status: "ready_for_pickup",
    assigned: false,
    createdAt: new Date(Date.now() - 14 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
    estimatedReadyAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
    pickup: {
      name: "Mozzo",
      address: "Catamarca 1378",
      city: "Corrientes",
      province: "Corrientes",
      latitude: -27.4698,
      longitude: -58.8332,
    },
    destination: {
      address: "",
      neighborhood: "Cambá Cuá",
      city: "Corrientes",
      reference: "",
      latitude: null,
      longitude: null,
      approximate: true,
    },
    customer: { name: "María", phone: "" },
    payment: {
      method: "mercado_pago",
      status: "paid",
      subtotal: 7400,
      amount: 9200,
      deliveryFee: 1800,
      collectOnDelivery: false,
    },
    notes: "",
    distanceToPickupKm: 2.1,
    items: [{ id: "item-003", name: "Hamburguesa especial", quantity: 1, notes: "" }],
    events: [{ status: "ready_for_pickup", createdAt: new Date().toISOString() }],
  },
]);

const useLocalPreview =
  getRuntimeEnvironment() === "development" &&
  String(process.env.REACT_APP_WE_RIDER_LIVE_AUTH || "").trim() !== "true";

export const weRiderSupabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        storageKey: RIDER_AUTH_STORAGE_KEY,
        storage: resilientSupabaseStorage,
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
        flowType: "pkce",
      },
    })
  : null;

const getStorage = () =>
  typeof window !== "undefined" ? window.localStorage : null;

const getRiderBackgroundDeviceId = () => {
  const storage = getStorage();
  const stored = cleanText(storage?.getItem(RIDER_BACKGROUND_DEVICE_KEY), 160);
  if (stored) return stored;
  const randomId =
    typeof window !== "undefined" &&
    typeof window.crypto?.randomUUID === "function"
      ? window.crypto.randomUUID()
      : `rider-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
  try {
    storage?.setItem(RIDER_BACKGROUND_DEVICE_KEY, randomId);
  } catch {
    // El identificador puede vivir solo durante esta sesion si no hay almacenamiento.
  }
  return randomId;
};

const cleanText = (value, maxLength = 180) =>
  String(value || "").replace(/\s+/g, " ").trim().slice(0, maxLength);

const normalizeEmail = (value) => cleanText(value, 180).toLowerCase();
const normalizePhone = (value) => cleanText(value, 60).replace(/\D/g, "");
const normalizeCoordinate = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
};

const isAtLeastRiderMinimumAge = (value, today = new Date()) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return false;
  const [year, month, day] = String(value).split("-").map(Number);
  const birthDate = new Date(year, month - 1, day, 12, 0, 0, 0);
  if (
    birthDate.getFullYear() !== year ||
    birthDate.getMonth() !== month - 1 ||
    birthDate.getDate() !== day
  ) {
    return false;
  }
  const latestAllowedBirthDate = new Date(today);
  latestAllowedBirthDate.setHours(12, 0, 0, 0);
  latestAllowedBirthDate.setFullYear(
    latestAllowedBirthDate.getFullYear() - RIDER_MINIMUM_AGE
  );
  return birthDate <= latestAllowedBirthDate;
};

export const getRequiredWeRiderDocumentTypes = (vehicleType) =>
  ["motorcycle", "car"].includes(cleanText(vehicleType, 40).toLowerCase())
    ? [...BASE_IDENTITY_DOCUMENT_TYPES, ...MOTORIZED_DOCUMENT_TYPES]
    : [...BASE_IDENTITY_DOCUMENT_TYPES];

const validateRiderIdentitySubmission = ({
  legalFullName,
  documentNumber,
  birthDate,
  consents,
  documents,
  vehicleType,
}) => {
  const safeLegalName = cleanText(legalFullName, 180);
  const safeDocumentNumber = String(documentNumber || "").replace(/\D/g, "");
  const safeBirthDate = cleanText(birthDate, 20);
  const submittedConsents = consents && typeof consents === "object" ? consents : {};
  const submittedDocuments = Array.isArray(documents) ? documents : [];

  if (safeLegalName.split(/\s+/).filter(Boolean).length < 2) {
    throw new Error("Ingresa tu nombre legal completo.");
  }
  if (!/^\d{7,9}$/.test(safeDocumentNumber)) {
    throw new Error("Ingresa un DNI válido.");
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(safeBirthDate)) {
    throw new Error("Ingresa tu fecha de nacimiento.");
  }
  const birthTime = new Date(`${safeBirthDate}T12:00:00`).getTime();
  if (!Number.isFinite(birthTime) || birthTime >= Date.now()) {
    throw new Error("Ingresa una fecha de nacimiento válida.");
  }
  if (!isAtLeastRiderMinimumAge(safeBirthDate)) {
    throw new Error("Para registrarte como Rider debes tener 18 años o más.");
  }
  if (submittedConsents.ageAndAccuracyAccepted !== true) {
    throw new Error("Debes confirmar tu mayoría de edad y la veracidad de los datos.");
  }
  if (
    submittedConsents.manualIdentityReviewAccepted !== true ||
    submittedConsents.sensitiveDataProcessingAccepted !== true
  ) {
    throw new Error("Debes autorizar la revisión manual de identidad y documentación.");
  }
  if (
    submittedConsents.termsAccepted !== true ||
    submittedConsents.privacyAccepted !== true
  ) {
    throw new Error("Debes aceptar los términos de Rider y la política de privacidad.");
  }
  if (
    submittedConsents.identityConsentVersion !== RIDER_IDENTITY_CONSENT_VERSION ||
    submittedConsents.termsVersion !== RIDER_TERMS_VERSION ||
    submittedConsents.privacyVersion !== RIDER_PRIVACY_VERSION
  ) {
    throw new Error("La versión del consentimiento cambió. Revisa y acepta nuevamente.");
  }

  const requiredTypes = getRequiredWeRiderDocumentTypes(vehicleType);
  requiredTypes.forEach((documentType) => {
    const document = submittedDocuments.find(
      (entry) => entry?.type === documentType && entry?.file
    );
    if (!document) {
      throw new Error("Falta adjuntar uno de los documentos requeridos.");
    }
    if (!RIDER_DOCUMENT_MIME_TYPES.has(String(document.file.type || ""))) {
      throw new Error("Los documentos deben ser imágenes JPG, PNG o WEBP.");
    }
    if (Number(document.file.size || 0) <= 0) {
      throw new Error("Uno de los archivos está vacío.");
    }
    if (Number(document.file.size || 0) > RIDER_DOCUMENT_MAX_SIZE) {
      throw new Error("Cada imagen puede pesar hasta 8 MB.");
    }
    if (documentType === "selfie") {
      const capturedAt = new Date(document.capturedAt || "").getTime();
      const livenessPassedAt = new Date(document.livenessPassedAt || "").getTime();
      const livenessChallenges = Array.isArray(document.livenessChallenges)
        ? document.livenessChallenges
        : [];
      if (
        document.captureSource !== "live_camera_liveness" ||
        document.livenessMethod !== RIDER_LIVENESS_METHOD ||
        !["blink", "mouth_open"].every((challenge) =>
          livenessChallenges.includes(challenge)
        ) ||
        Number(document.livenessScore || 0) < 0.38 ||
        !Number.isFinite(capturedAt) ||
        !Number.isFinite(livenessPassedAt) ||
        Date.now() - capturedAt > RIDER_LIVE_SELFIE_MAX_AGE_MS ||
        Date.now() - livenessPassedAt > RIDER_LIVE_SELFIE_MAX_AGE_MS ||
        capturedAt > Date.now() + 60_000 ||
        capturedAt < livenessPassedAt - 5_000 ||
        capturedAt - livenessPassedAt > 60_000
      ) {
        throw new Error("La selfie debe aprobar la prueba de vida con la cámara.");
      }
    }
  });

  return {
    legalFullName: safeLegalName,
    documentNumber: safeDocumentNumber,
    birthDate: safeBirthDate,
    consents: {
      ageAndAccuracyAccepted: true,
      manualIdentityReviewAccepted: true,
      sensitiveDataProcessingAccepted: true,
      termsAccepted: true,
      privacyAccepted: true,
      identityConsentVersion: RIDER_IDENTITY_CONSENT_VERSION,
      termsVersion: RIDER_TERMS_VERSION,
      privacyVersion: RIDER_PRIVACY_VERSION,
    },
    documents: submittedDocuments.filter((entry) =>
      requiredTypes.includes(entry?.type)
    ),
  };
};

const getRiderDocumentExtension = (file) => {
  switch (String(file?.type || "").toLowerCase()) {
    case "image/png":
      return "png";
    case "image/webp":
      return "webp";
    default:
      return "jpg";
  }
};

const createRiderDocumentPath = (userId, documentType, file) => {
  const randomId =
    typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function"
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
  return `${userId}/${documentType}/${randomId}.${getRiderDocumentExtension(file)}`;
};

const withRequestTimeout = async (request, timeoutMs, message) => {
  let timeoutId = null;
  try {
    return await Promise.race([
      request,
      new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== null) window.clearTimeout(timeoutId);
  }
};

const createLocalPickupCode = () =>
  String(Math.floor(Math.random() * 1000000)).padStart(6, "0");

const createLocalDeliveryCode = () =>
  String(Math.floor(Math.random() * 1000000)).padStart(6, "0");

const readJson = (key, fallback = null) => {
  try {
    const value = JSON.parse(getStorage()?.getItem(key) || "null");
    return value ?? fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  getStorage()?.setItem(key, JSON.stringify(value));
};

const dispatchUpdated = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(WE_RIDER_UPDATED_EVENT));
  }
};

const hashLocalPassword = async (password) => {
  const value = String(password || "");
  if (typeof crypto !== "undefined" && crypto.subtle && typeof TextEncoder !== "undefined") {
    const digest = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(value)
    );
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  }
  return `preview-${value.length}-${value.charCodeAt(0) || 0}`;
};

const cloneDemoJobs = () => JSON.parse(JSON.stringify(DEMO_JOBS));

const isSeededDemoJob = (job) =>
  job?.demo === true ||
  ["local-assignment-001", "local-assignment-002"].includes(job?.id);

const ensureLocalJobs = () => {
  const current = readJson(LOCAL_JOBS_KEY, []);
  if (Array.isArray(current) && current.length) return current;
  const seeded = cloneDemoJobs();
  writeJson(LOCAL_JOBS_KEY, seeded);
  return seeded;
};

const readLocalAccount = () => readJson(LOCAL_ACCOUNT_KEY);
const writeLocalAccount = (account) => writeJson(LOCAL_ACCOUNT_KEY, account);

const normalizeProfile = (profile = {}, email = "") => ({
  id: cleanText(profile.id, 120),
  userId: cleanText(profile.userId ?? profile.user_id, 120),
  fullName: cleanText(profile.fullName ?? profile.full_name, 160),
  email: normalizeEmail(profile.email || email),
  phone: normalizePhone(profile.phone ?? profile.phone_e164),
  vehicleType: cleanText(profile.vehicleType ?? profile.vehicle_type, 40) || "motorcycle",
  vehicleReference: cleanText(
    profile.vehicleReference ?? profile.vehicle_reference,
    120
  ),
  status: cleanText(profile.status, 40) || "offline",
  location: profile.location || null,
});

const buildLocalAuthState = () => {
  const account = readLocalAccount();
  const session = readJson(LOCAL_SESSION_KEY);
  if (!account || !session || account.email !== session.email) {
    return { session: null, profile: null, localPreview: true };
  }

  const user = { id: account.userId, email: account.email };
  return {
    session: { user, access_token: "", localPreview: true },
    profile: normalizeProfile(account, account.email),
    localPreview: true,
  };
};

const buildLocalDashboard = () => {
  const account = readLocalAccount();
  if (!account) {
    return {
      ok: true,
      needsProfile: true,
      profile: null,
      activeJob: null,
      availableJobs: [],
    };
  }
  const jobs = ensureLocalJobs();
  const activeJob =
    jobs.find(
      (job) =>
        job.assigned === true &&
        !["delivered", "cancelled"].includes(job.status)
    ) || null;
  const availableJobs = activeJob
    ? []
    : jobs.filter(
        (job) =>
          job.assigned !== true &&
          job.status === "searching_driver"
      );

  return {
    ok: true,
    needsProfile: false,
    profile: normalizeProfile(account, account.email),
    compliance: normalizeWeRiderCompliance(
      account.compliance || buildInitialWeRiderCompliance({ profileComplete: true })
    ),
    activeJob,
    availableJobs,
  };
};

const getRemoteSession = async () => {
  if (!weRiderSupabase) return null;
  const { data, error } = await withRequestTimeout(
    weRiderSupabase.auth.getSession(),
    15000,
    "La sesión tardó demasiado en responder. Reintenta sin cerrar la app."
  );
  if (error) throw new Error(error.message || "No pudimos recuperar tu sesión.");
  return data?.session || null;
};

const normalizeResponseError = async (error, fallbackMessage) => {
  if (error?.context && typeof error.context.json === "function") {
    try {
      const payload = await error.context.json();
      if (payload?.error) return String(payload.error);
    } catch {
      // The gateway can return plain text while the function starts.
    }
  }
  return error?.message || fallbackMessage;
};

const invokeRemoteRider = async (body, fallbackMessage) => {
  if (!isSupabaseConfigured || !weRiderSupabase) {
    throw new Error("El acceso del rider no está configurado.");
  }
  const session = await getRemoteSession();
  if (!session?.access_token) {
    throw new Error("Tu sesión venció. Vuelve a ingresar.");
  }

  let timeoutId = null;
  try {
    const endpoint =
      getRuntimeEnvironment() === "development"
        ? "/api/we-pedidos-rider"
        : `${supabaseUrl}/functions/v1/${RIDER_FUNCTION}`;
    const controller = new AbortController();
    timeoutId = window.setTimeout(() => controller.abort(), 25000);
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${session.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      signal: controller.signal,
      cache: "no-store",
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data || data.ok === false) {
      throw new Error(data?.error || data?.message || fallbackMessage);
    }
    return data;
  } catch (error) {
    throw new Error(await normalizeResponseError(error, fallbackMessage));
  } finally {
    if (timeoutId !== null) window.clearTimeout(timeoutId);
  }
};

const updateLocalDashboard = (updater) => {
  const account = readLocalAccount();
  const jobs = ensureLocalJobs();
  const result = updater({ account, jobs });
  if (result.account) writeLocalAccount(result.account);
  if (result.jobs) writeJson(LOCAL_JOBS_KEY, result.jobs);
  dispatchUpdated();
  return buildLocalDashboard();
};

export const isUsingLocalWeRiderAuth = () => useLocalPreview;

export const getLocalWeRiderAssignment = (externalOrderId) => {
  if (!useLocalPreview || !externalOrderId) return null;

  const jobs = readJson(LOCAL_JOBS_KEY, []);
  const job = Array.isArray(jobs)
    ? jobs.find((item) => item.externalOrderId === externalOrderId)
    : null;
  if (!job) return null;

  const account = readLocalAccount();
  const assigned = job.assigned === true;
  return {
    id: job.id,
    external_order_id: job.externalOrderId,
    driver_id: assigned ? job.driverId || account?.id || DEMO_ACCOUNT.id : null,
    driver_name: assigned
      ? job.driverName || account?.fullName || DEMO_ACCOUNT.fullName
      : "",
    driver_phone: assigned
      ? job.driverPhone || account?.phone || DEMO_ACCOUNT.phone
      : "",
    status: job.status,
    tracking_token: job.trackingToken || null,
    estimated_arrival_at: job.estimatedArrivalAt || null,
    pickup_deadline_at: job.pickupDeadlineAt || null,
    pickup_arrived_at: job.pickupArrivedAt || null,
    pickup_verification_code: job.pickupVerificationCode || null,
    pickup_verification_attempts: Number(job.pickupVerificationAttempts || 0),
    pickup_verified_at: job.pickupVerifiedAt || null,
    pickup_verification_locked_at: job.pickupVerificationLockedAt || null,
    delivery_verification_code: job.deliveryVerificationCode || null,
    delivery_verification_attempts: Number(job.deliveryVerificationAttempts || 0),
    delivery_verified_at: job.deliveryVerifiedAt || null,
    delivery_verification_locked_at: job.deliveryVerificationLockedAt || null,
    assigned_at: job.assignedAt || null,
    picked_up_at: job.pickedUpAt || null,
    delivered_at: job.deliveredAt || null,
    cancelled_at: job.cancelledAt || null,
    updated_at: job.updatedAt || job.createdAt || null,
    local_preview: true,
  };
};

export const getLocalWeRiderTracking = (trackingToken) => {
  if (!useLocalPreview || !trackingToken) return null;

  const jobs = readJson(LOCAL_JOBS_KEY, []);
  const job = Array.isArray(jobs)
    ? jobs.find((item) => item.trackingToken === String(trackingToken))
    : null;
  if (!job) return null;

  const account = readLocalAccount();
  const meta = LOCAL_TRACKING_STATUS[job.status] || {
    label: "Pedido actualizado",
    message: "El estado de tu pedido fue actualizado.",
  };
  const driverName = cleanText(
    job.driverName || account?.fullName || DEMO_ACCOUNT.fullName,
    160
  );

  return {
    ok: true,
    tracking: {
      displayNumber: job.displayNumber,
      restaurantName: job.pickup?.name || "Restaurante",
      total: Number(job.payment?.amount || 0),
      status: job.status,
      statusLabel: meta.label,
      customerMessage: meta.message,
      estimatedArrivalAt: job.estimatedArrivalAt || null,
      driver:
        job.assigned === true && driverName
          ? {
              firstName: driverName.split(" ")[0],
              vehicleType: account?.vehicleType || DEMO_ACCOUNT.vehicleType,
              vehicleReference:
                account?.vehicleReference || DEMO_ACCOUNT.vehicleReference,
            }
          : null,
      driverLocation: account?.location || null,
      deliveryVerificationCode:
        ["picked_up", "in_transit"].includes(job.status)
          ? job.deliveryVerificationCode || null
          : null,
      events: (job.events || []).map((event) => ({
        ...event,
        label:
          LOCAL_TRACKING_STATUS[event?.status]?.label || "Pedido actualizado",
      })),
      localPreview: true,
    },
  };
};

export const publishLocalWeRiderOffer = ({ order, branch, restaurantProfile }) => {
  if (!useLocalPreview) return null;
  if (!order?.id) throw new Error("No encontramos el pedido que quieres publicar.");

  const currentJobs = ensureLocalJobs();
  const currentJob = currentJobs.find(
    (job) => job.externalOrderId === order.id
  );
  if (
    currentJob?.assigned === true &&
    !["delivered", "cancelled"].includes(currentJob.status)
  ) {
    throw new Error("El Rider de prueba ya aceptó este pedido.");
  }

  const now = new Date().toISOString();
  const address = order.address || {};
  const latitude = normalizeCoordinate(address.latitude);
  const longitude = normalizeCoordinate(address.longitude);
  const pickupLatitude = normalizeCoordinate(
    restaurantProfile?.paymentLocationLatitude ??
      branch?.paymentLocationLatitude ??
      branch?.payment_location_latitude
  );
  const pickupLongitude = normalizeCoordinate(
    restaurantProfile?.paymentLocationLongitude ??
      branch?.paymentLocationLongitude ??
      branch?.payment_location_longitude
  );
  const pickupAddress = cleanText(
    restaurantProfile?.direccion || branch?.address,
    240
  );
  const customerName = cleanText(
    [order.customer?.first_name, order.customer?.last_name]
      .filter(Boolean)
      .join(" ") || "Cliente",
    160
  );
  const destinationAddress = cleanText(
    [address.street, address.street_number || address.streetNumber]
      .filter(Boolean)
      .join(" "),
    240
  );
  const items = (Array.isArray(order.items) ? order.items : []).map(
    (item, index) => ({
      id: item.id || `${order.id}-item-${index + 1}`,
      name: cleanText(item.product_name || item.name || "Producto", 180),
      quantity: Math.max(1, Number(item.quantity || 1)),
      notes: cleanText(
        [
          ...(Array.isArray(item.selection_details)
            ? item.selection_details
            : []),
          item.notes,
        ]
          .filter(Boolean)
          .join(" | "),
        400
      ),
    })
  );
  const paymentMethod = cleanText(order.payment_method, 60) || "cash_on_delivery";
  const trackingToken = cleanText(
    order.delivery_assignment?.tracking_token ||
      order.trackingToken ||
      order.tracking_token,
    120
  );
  const nextJob = {
    id: currentJob?.id || `local-assignment-${order.id}`,
    externalOrderId: order.id,
    displayNumber: cleanText(order.display_number, 80) || `DEL-${order.id}`,
    branchId: branch?.id || order.branch_id || "",
    trackingToken,
    status: "searching_driver",
    assigned: false,
    createdAt: currentJob?.createdAt || now,
    updatedAt: now,
    estimatedReadyAt: now,
    pickupDeadlineAt: null,
    pickupArrivedAt: null,
    pickupVerificationCode: null,
    pickupVerificationAttempts: 0,
    pickupVerifiedAt: null,
    pickupVerificationLockedAt: null,
    deliveryVerificationCode: null,
    deliveryVerificationAttempts: 0,
    deliveryVerifiedAt: null,
    deliveryVerificationLockedAt: null,
    pickup: {
      name: cleanText(
        restaurantProfile?.nombre || branch?.name || "Restaurante",
        160
      ),
      address: pickupAddress,
      city: cleanText(
        restaurantProfile?.paymentLocationCity ||
          branch?.paymentLocationCity ||
          branch?.payment_location_city,
        100
      ),
      province: cleanText(
        restaurantProfile?.paymentLocationState ||
          branch?.paymentLocationState ||
          branch?.payment_location_state,
        100
      ),
      latitude: pickupLatitude,
      longitude: pickupLongitude,
    },
    destination: {
      address: destinationAddress,
      neighborhood: cleanText(address.neighborhood, 100),
      city: cleanText(address.city, 100),
      reference: cleanText(address.reference, 240),
      latitude,
      longitude,
      approximate: latitude === null || longitude === null,
    },
    customer: {
      name: customerName,
      phone: normalizePhone(
        order.customer?.phone_original || order.customer?.phone_normalized
      ),
    },
    payment: {
      method: paymentMethod,
      status: cleanText(order.payment_status, 40) || "pending",
      subtotal: Number(order.subtotal || 0),
      amount: Number(order.total_amount || 0),
      deliveryFee: Number(order.delivery_fee || 0),
      collectOnDelivery:
        order.payment_status !== "paid" &&
        ["cash", "cash_on_delivery", "card_on_delivery", "qr_on_delivery"].includes(paymentMethod),
    },
    notes: cleanText(order.customer_notes, 400),
    distanceToPickupKm: null,
    items,
    events: [
      ...(currentJob?.events || []).filter(
        (event) => event?.status !== "searching_driver"
      ),
      { status: "searching_driver", createdAt: now },
    ],
  };

  const nextJobs = [
    nextJob,
    ...currentJobs.filter(
      (job) =>
        job.externalOrderId !== order.id &&
        !isSeededDemoJob(job)
    ),
  ];
  writeJson(LOCAL_JOBS_KEY, nextJobs);
  dispatchUpdated();
  return getLocalWeRiderAssignment(order.id);
};

export const removeLocalWeRiderOffer = (externalOrderId) => {
  if (!useLocalPreview || !externalOrderId) {
    return { removed: false, assigned: false };
  }

  const jobs = readJson(LOCAL_JOBS_KEY, []);
  const currentJobs = Array.isArray(jobs) ? jobs : [];
  const currentJob = currentJobs.find(
    (job) => job.externalOrderId === externalOrderId
  );
  if (!currentJob) return { removed: false, assigned: false };
  if (
    currentJob.assigned === true &&
    !["delivered", "cancelled"].includes(currentJob.status)
  ) {
    return { removed: false, assigned: true };
  }

  writeJson(
    LOCAL_JOBS_KEY,
    currentJobs.filter((job) => job.externalOrderId !== externalOrderId)
  );
  dispatchUpdated();
  return { removed: true, assigned: false };
};

export const releaseExpiredLocalWeRiderAssignment = (externalOrderId) => {
  if (!useLocalPreview || !externalOrderId) return null;

  const jobs = readJson(LOCAL_JOBS_KEY, []);
  const currentJobs = Array.isArray(jobs) ? jobs : [];
  const currentJob = currentJobs.find(
    (job) => job.externalOrderId === externalOrderId
  );
  if (!currentJob?.assigned) {
    throw new Error("Este pedido no tiene un Rider asignado.");
  }
  if (currentJob.status === "arrived_pickup" || currentJob.pickupArrivedAt) {
    throw new Error("El Rider llegó dentro del plazo y está validando el retiro.");
  }
  if (
    !currentJob.pickupDeadlineAt ||
    new Date(currentJob.pickupDeadlineAt).getTime() >= Date.now()
  ) {
    throw new Error("El tiempo del Rider todavía no venció.");
  }

  const now = new Date().toISOString();
  writeJson(
    LOCAL_JOBS_KEY,
    currentJobs.map((job) =>
      job.externalOrderId === externalOrderId
        ? {
            ...job,
            assigned: false,
            status: "searching_driver",
            driverId: null,
            driverName: "",
            driverPhone: "",
            assignedAt: null,
            pickupDeadlineAt: null,
            pickupArrivedAt: null,
            pickupVerificationCode: null,
            pickupVerificationAttempts: 0,
            pickupVerifiedAt: null,
            pickupVerificationLockedAt: null,
            deliveryVerificationCode: null,
            deliveryVerificationAttempts: 0,
            deliveryVerifiedAt: null,
            deliveryVerificationLockedAt: null,
            updatedAt: now,
            events: [
              ...(job.events || []),
              { status: "searching_driver", createdAt: now },
            ],
          }
        : job
    )
  );
  const account = readLocalAccount();
  if (account) writeLocalAccount({ ...account, status: "available" });
  dispatchUpdated();
  return getLocalWeRiderAssignment(externalOrderId);
};

export const cancelLocalWeRiderDelivery = (externalOrderId) => {
  if (!useLocalPreview || !externalOrderId) return null;

  const jobs = readJson(LOCAL_JOBS_KEY, []);
  const currentJobs = Array.isArray(jobs) ? jobs : [];
  const currentJob = currentJobs.find(
    (job) => job.externalOrderId === externalOrderId
  );
  if (!currentJob || currentJob.status === "delivered") return null;

  const now = new Date().toISOString();
  const account = readLocalAccount();
  const nextJobs = currentJobs.map((job) =>
    job.externalOrderId === externalOrderId
      ? {
          ...job,
          status: "cancelled",
          cancelledAt: now,
          pickupVerificationCode: null,
          deliveryVerificationCode: null,
          deliveryVerificationAttempts: 0,
          deliveryVerificationLockedAt: null,
          updatedAt: now,
          events: [
            ...(job.events || []),
            { status: "cancelled", createdAt: now },
          ],
        }
      : job
  );
  writeJson(LOCAL_JOBS_KEY, nextJobs);
  if (account && currentJob.assigned === true) {
    writeLocalAccount({ ...account, status: "available" });
  }
  dispatchUpdated();
  return getLocalWeRiderAssignment(externalOrderId);
};

export const getWeRiderAuthState = async () => {
  if (useLocalPreview) return buildLocalAuthState();
  const session = await getRemoteSession();
  return {
    session,
    profile: null,
    localPreview: false,
  };
};

export const subscribeToWeRiderAuth = (listener) => {
  if (useLocalPreview) {
    const handler = () => listener();
    window.addEventListener(WE_RIDER_UPDATED_EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(WE_RIDER_UPDATED_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }
  if (!weRiderSupabase) return () => {};
  const { data } = weRiderSupabase.auth.onAuthStateChange(() => listener());
  return () => data.subscription.unsubscribe();
};

export const subscribeToLocalWeRiderUpdates = (listener) => {
  if (!useLocalPreview || typeof window === "undefined") return () => {};
  const handleUpdated = () => listener();
  const handleStorage = (event) => {
    if (!event?.key || event.key === LOCAL_JOBS_KEY) listener();
  };
  window.addEventListener(WE_RIDER_UPDATED_EVENT, handleUpdated);
  window.addEventListener("storage", handleStorage);
  return () => {
    window.removeEventListener(WE_RIDER_UPDATED_EVENT, handleUpdated);
    window.removeEventListener("storage", handleStorage);
  };
};

export const subscribeToWeRiderDashboardChanges = (userId, listener) => {
  const safeUserId = cleanText(userId, 120);

  if (useLocalPreview) {
    return subscribeToLocalWeRiderUpdates(listener);
  }

  if (!weRiderSupabase || !safeUserId) {
    return () => {};
  }

  const notify = () => {
    if (typeof listener === "function") listener();
  };
  const channel = weRiderSupabase
    .channel(`we-rider-dashboard:${safeUserId}:${Date.now()}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "we_drivers",
        filter: `user_id=eq.${safeUserId}`,
      },
      notify
    )
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "we_rider_live_signals",
      },
      notify
    )
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "we_rider_compliance_profiles",
      },
      notify
    )
    .subscribe();

  return () => {
    void weRiderSupabase.removeChannel(channel);
  };
};

export const signUpWeRider = async ({
  fullName,
  email,
  phone,
  vehicleType,
  vehicleReference,
  password,
  redirectTo,
}) => {
  const safeName = cleanText(fullName, 160);
  const safeEmail = normalizeEmail(email);
  const safePhone = normalizePhone(phone);
  const safeVehicle = cleanText(vehicleType, 40).toLowerCase();

  if (safeName.split(/\s+/).filter(Boolean).length < 2) {
    throw new Error("Ingresa tu nombre y apellido.");
  }
  if (!/^\S+@\S+\.\S+$/.test(safeEmail)) {
    throw new Error("Ingresa un correo válido.");
  }
  if (safePhone.length < 8) {
    throw new Error("Ingresa un número de celular válido.");
  }
  if (!RIDER_VEHICLE_TYPES.has(safeVehicle)) {
    throw new Error("Selecciona el medio con el que harás las entregas.");
  }
  if (String(password || "").length < 8) {
    throw new Error("La contraseña debe tener al menos 8 caracteres.");
  }

  if (useLocalPreview) {
    const account = {
      id: `local-rider-${Date.now().toString(36)}`,
      userId: `local-rider-user-${Date.now().toString(36)}`,
      fullName: safeName,
      email: safeEmail,
      phone: safePhone,
      vehicleType: safeVehicle,
      vehicleReference: cleanText(vehicleReference, 120),
      status: "offline",
      compliance: buildInitialWeRiderCompliance({ profileComplete: true }),
      passwordHash: await hashLocalPassword(password),
    };
    writeLocalAccount(account);
    writeJson(LOCAL_SESSION_KEY, {
      email: safeEmail,
      createdAt: new Date().toISOString(),
    });
    const savedJobs = readJson(LOCAL_JOBS_KEY, []);
    if (!Array.isArray(savedJobs) || !savedJobs.length) {
      writeJson(LOCAL_JOBS_KEY, cloneDemoJobs());
    }
    dispatchUpdated();
    return { session: buildLocalAuthState().session, requiresEmailConfirmation: false };
  }

  if (!weRiderSupabase) {
    throw new Error("El registro del rider no está configurado.");
  }
  const acceptedTermsAt = new Date().toISOString();
  const { data, error } = await weRiderSupabase.auth.signUp({
    email: safeEmail,
    password,
    options: {
      emailRedirectTo: redirectTo,
      data: {
        account_type: "we_rider",
        full_name: safeName,
        phone: safePhone,
        vehicle_type: safeVehicle,
        vehicle_reference: cleanText(vehicleReference, 120),
        accepted_terms_at: acceptedTermsAt,
      },
    },
  });
  if (error) throw new Error(error.message || "No pudimos crear tu cuenta de rider.");
  if (data?.session) {
    await invokeRemoteRider(
      {
        action: "register_profile",
        fullName: safeName,
        phone: safePhone,
        vehicleType: safeVehicle,
        vehicleReference,
      },
      "No pudimos preparar tu perfil de rider."
    );
  }
  return {
    session: data?.session || null,
    requiresEmailConfirmation: !data?.session,
  };
};

export const signInWeRider = async ({ email, password }) => {
  const safeEmail = normalizeEmail(email);
  if (useLocalPreview) {
    let account = readLocalAccount();
    const isDemo =
      safeEmail === DEMO_ACCOUNT.email &&
      String(password || "") === DEMO_ACCOUNT.password;
    if (isDemo && account?.email !== safeEmail) {
      account = {
        ...DEMO_ACCOUNT,
        passwordHash: await hashLocalPassword(DEMO_ACCOUNT.password),
      };
      writeLocalAccount(account);
      const savedJobs = readJson(LOCAL_JOBS_KEY, []);
      if (!Array.isArray(savedJobs) || !savedJobs.length) {
        writeJson(LOCAL_JOBS_KEY, cloneDemoJobs());
      }
    }
    const passwordHash = await hashLocalPassword(password);
    if (!account || account.email !== safeEmail || account.passwordHash !== passwordHash) {
      throw new Error("El correo o la contraseña no coinciden.");
    }
    writeJson(LOCAL_SESSION_KEY, {
      email: safeEmail,
      createdAt: new Date().toISOString(),
    });
    ensureLocalJobs();
    dispatchUpdated();
    return buildLocalAuthState();
  }

  if (!weRiderSupabase) throw new Error("El acceso del rider no está configurado.");
  const { data, error } = await weRiderSupabase.auth.signInWithPassword({
    email: safeEmail,
    password,
  });
  if (error) throw new Error(error.message || "No pudimos iniciar sesión.");
  return { session: data.session, profile: null, localPreview: false };
};

export const signOutWeRider = async () => {
  if (useLocalPreview) {
    getStorage()?.removeItem(LOCAL_SESSION_KEY);
    dispatchUpdated();
    return;
  }
  if (!weRiderSupabase) return;
  const { error } = await weRiderSupabase.auth.signOut();
  if (error) throw new Error(error.message || "No pudimos cerrar la sesión.");
};

export const requestWeRiderPasswordReset = async ({ email, redirectTo }) => {
  if (useLocalPreview) {
    const account = readLocalAccount();
    if (!account || account.email !== normalizeEmail(email)) {
      throw new Error("No encontramos ese correo en la vista local.");
    }
    return { localPreview: true };
  }
  if (!weRiderSupabase) throw new Error("La recuperación no está configurada.");
  const { error } = await weRiderSupabase.auth.resetPasswordForEmail(
    normalizeEmail(email),
    { redirectTo }
  );
  if (error) throw new Error(error.message || "No pudimos enviar el correo.");
  return { localPreview: false };
};

export const updateWeRiderPassword = async (password) => {
  if (String(password || "").length < 8) {
    throw new Error("La contraseña debe tener al menos 8 caracteres.");
  }
  if (useLocalPreview) {
    const account = readLocalAccount();
    if (!account) throw new Error("No encontramos tu cuenta local.");
    writeLocalAccount({
      ...account,
      passwordHash: await hashLocalPassword(password),
    });
    return;
  }
  if (!weRiderSupabase) throw new Error("La recuperación no está configurada.");
  const { error } = await weRiderSupabase.auth.updateUser({ password });
  if (error) throw new Error(error.message || "No pudimos actualizar la contraseña.");
};

export const completeWeRiderAuthCallback = async (callbackUrl) => {
  if (useLocalPreview) return buildLocalAuthState();
  if (!weRiderSupabase) throw new Error("El acceso del rider no está configurado.");
  const url = new URL(callbackUrl);
  const code = url.searchParams.get("code");
  if (!code) throw new Error("El enlace de confirmación no es válido.");
  const { error } = await weRiderSupabase.auth.exchangeCodeForSession(code);
  if (error) throw new Error(error.message || "No pudimos confirmar tu cuenta.");
  return getWeRiderAuthState();
};

export const loadWeRiderDashboard = async () =>
  useLocalPreview
    ? buildLocalDashboard()
    : invokeRemoteRider(
        { action: "bootstrap" },
        "No pudimos cargar los viajes disponibles."
      );

export const registerWeRiderProfile = async (profile) => {
  const vehicleType = cleanText(profile?.vehicleType, 40).toLowerCase();
  if (!RIDER_VEHICLE_TYPES.has(vehicleType)) {
    throw new Error("Selecciona el medio con el que harás las entregas.");
  }
  const normalizedProfile = { ...profile, vehicleType };
  if (useLocalPreview) {
    return updateLocalDashboard(({ account, jobs }) => ({
      account: {
        ...account,
        ...normalizedProfile,
        fullName: cleanText(normalizedProfile.fullName, 160),
        phone: normalizePhone(normalizedProfile.phone),
        compliance: normalizeWeRiderCompliance({
          ...(account?.compliance || {}),
          profileStatus: "complete",
        }),
      },
      jobs,
    }));
  }
  return invokeRemoteRider(
    { action: "register_profile", ...normalizedProfile },
    "No pudimos guardar tu perfil de rider."
  );
};

export const submitWeRiderIdentity = async (submission) => {
  const dashboard = await loadWeRiderDashboard();
  const vehicleType = dashboard?.profile?.vehicleType || "motorcycle";
  const validated = validateRiderIdentitySubmission({
    ...submission,
    vehicleType,
  });

  if (useLocalPreview) {
    const localAccount = readLocalAccount();
    if (localAccount?.id) {
      await saveLocalWeRiderIdentityDocuments(
        localAccount.id,
        validated.documents
      );
    }
    const acceptedAt = new Date().toISOString();
    return updateLocalDashboard(({ account, jobs }) => ({
      account: {
        ...account,
        identitySubmission: {
          legalFullName: validated.legalFullName,
          documentNumberLast4: validated.documentNumber.slice(-4),
          birthDate: validated.birthDate,
          identityReviewMethod: "manual_backoffice",
          legalAcceptances: {
            ...validated.consents,
            acceptedAt,
          },
          documents: validated.documents.map(({
            type,
            file,
            captureSource,
            capturedAt,
            livenessMethod,
            livenessPassedAt,
            livenessChallenges,
            livenessScore,
          }) => ({
            type,
            fileName: cleanText(file.name, 220),
            mimeType: file.type,
            sizeBytes: Number(file.size || 0),
            captureSource: cleanText(captureSource, 40) || null,
            capturedAt: cleanText(capturedAt, 40) || null,
            livenessMethod: cleanText(livenessMethod, 80) || null,
            livenessPassedAt: cleanText(livenessPassedAt, 40) || null,
            livenessChallenges: Array.isArray(livenessChallenges)
              ? livenessChallenges.slice(0, 4)
              : [],
            livenessScore: Number(livenessScore || 0) || null,
          })),
          submittedAt: new Date().toISOString(),
        },
        compliance: normalizeWeRiderCompliance({
          ...(account?.compliance || {}),
          profileStatus: "complete",
          identityStatus: "pending",
          reviewStatus: "pending",
          statusReason:
            "Documentación y consentimientos recibidos. Pendiente de revisión manual de identidad.",
          identitySubmittedAt: acceptedAt,
          updatedAt: new Date().toISOString(),
        }),
      },
      jobs,
    }));
  }

  const session = await getRemoteSession();
  const userId = cleanText(session?.user?.id, 120);
  if (!userId || !weRiderSupabase) {
    throw new Error("Tu sesión venció. Vuelve a ingresar.");
  }

  const uploadedPaths = [];
  try {
    const uploadedDocuments = [];
    for (const document of validated.documents) {
      const storagePath = createRiderDocumentPath(
        userId,
        document.type,
        document.file
      );
      const upload = await weRiderSupabase.storage
        .from(RIDER_DOCUMENT_BUCKET)
        .upload(storagePath, document.file, {
          cacheControl: "3600",
          contentType: document.file.type,
          upsert: false,
        });
      if (upload.error) {
        throw new Error(upload.error.message || "No pudimos subir la documentación.");
      }
      uploadedPaths.push(upload.data.path);
      uploadedDocuments.push({
        type: document.type,
        storagePath: upload.data.path,
        fileName: cleanText(document.file.name, 220),
        mimeType: document.file.type,
        sizeBytes: Number(document.file.size || 0),
        captureSource: cleanText(document.captureSource, 40) || null,
        capturedAt: cleanText(document.capturedAt, 40) || null,
        livenessMethod: cleanText(document.livenessMethod, 80) || null,
        livenessPassedAt: cleanText(document.livenessPassedAt, 40) || null,
        livenessChallenges: Array.isArray(document.livenessChallenges)
          ? document.livenessChallenges.slice(0, 4)
          : [],
        livenessScore: Number(document.livenessScore || 0) || null,
      });
    }

    return await invokeRemoteRider(
      {
        action: "submit_identity",
        legalFullName: validated.legalFullName,
        documentNumber: validated.documentNumber,
        birthDate: validated.birthDate,
        consents: validated.consents,
        documents: uploadedDocuments,
      },
      "No pudimos presentar tu documentación."
    );
  } catch (error) {
    if (uploadedPaths.length) {
      await weRiderSupabase.storage
        .from(RIDER_DOCUMENT_BUCKET)
        .remove(uploadedPaths)
        .catch(() => null);
    }
    throw error;
  }
};

export const setWeRiderAvailability = async (status) => {
  if (useLocalPreview) {
    return updateLocalDashboard(({ account, jobs }) => {
      const compliance = normalizeWeRiderCompliance(account?.compliance);
      if (status === "available" && !compliance.canOperate) {
        throw new Error(
          compliance.statusReason ||
            "Tu habilitación Rider todavía está pendiente."
        );
      }
      const hasActive = jobs.some(
        (job) => job.assigned && !["delivered", "cancelled"].includes(job.status)
      );
      if (hasActive && status !== "available") {
        throw new Error("Finaliza la entrega antes de desconectarte.");
      }
      return { account: { ...account, status }, jobs };
    });
  }
  return invokeRemoteRider(
    { action: "set_availability", status },
    "No pudimos cambiar tu disponibilidad."
  );
};

export const registerWeRiderBackgroundDevice = async () => {
  if (useLocalPreview) return null;
  const deviceId = getRiderBackgroundDeviceId();
  const response = await invokeRemoteRider(
    { action: "register_background_device", deviceId },
    "No pudimos activar las alertas Rider en segundo plano."
  );
  return {
    endpoint: `${supabaseUrl}/functions/v1/${RIDER_FUNCTION}`,
    anonKey: supabaseAnonKey,
    deviceToken: response.deviceToken,
    expiresAt: response.expiresAt,
  };
};

export const revokeWeRiderBackgroundDevice = async () => {
  if (useLocalPreview) return { ok: true };
  return invokeRemoteRider(
    {
      action: "revoke_background_device",
      deviceId: getRiderBackgroundDeviceId(),
    },
    "No pudimos desactivar las alertas de este dispositivo."
  );
};

export const claimWeRiderJob = async (assignmentId) => {
  if (useLocalPreview) {
    return updateLocalDashboard(({ account, jobs }) => {
      const compliance = normalizeWeRiderCompliance(account?.compliance);
      if (!compliance.canOperate) {
        throw new Error(
          compliance.statusReason ||
            "Tu habilitación Rider todavía está pendiente."
        );
      }
      if (account?.status !== "available") {
        throw new Error("Ponte disponible antes de aceptar un viaje.");
      }
      if (jobs.some((job) => job.assigned && !["delivered", "cancelled"].includes(job.status))) {
        throw new Error("Ya tienes una entrega activa.");
      }
      const selected = jobs.find((job) => job.id === assignmentId && !job.assigned);
      if (!selected) throw new Error("Ese viaje ya no está disponible.");
      const now = new Date().toISOString();
      const pickupDeadlineAt = new Date(Date.now() + 20 * 60 * 1000).toISOString();
      const pickupVerificationCode = createLocalPickupCode();
      const nextJobs = jobs.map((job) => {
        if (job.id !== assignmentId) return job;
        const seededDemo = isSeededDemoJob(job);
        return {
          ...job,
          assigned: true,
          status: "driver_assigned",
          driverId: account?.id || DEMO_ACCOUNT.id,
          driverName: account?.fullName || DEMO_ACCOUNT.fullName,
          driverPhone: account?.phone || DEMO_ACCOUNT.phone,
          assignedAt: now,
          pickupDeadlineAt,
          pickupArrivedAt: null,
          pickupVerificationCode,
          pickupVerificationAttempts: 0,
          pickupVerifiedAt: null,
          pickupVerificationLockedAt: null,
          deliveryVerificationCode: null,
          deliveryVerificationAttempts: 0,
          deliveryVerifiedAt: null,
          deliveryVerificationLockedAt: null,
          updatedAt: now,
          destination: {
            ...job.destination,
            address:
              job.destination?.address ||
              (seededDemo ? "España 1234, Corrientes" : ""),
            reference:
              job.destination?.reference || (seededDemo ? "Portón gris" : ""),
            latitude:
              job.destination?.latitude ?? (seededDemo ? -27.4742 : null),
            longitude:
              job.destination?.longitude ?? (seededDemo ? -58.8271 : null),
            approximate:
              job.destination?.approximate ?? (seededDemo ? false : true),
          },
          customer: {
            ...job.customer,
            phone:
              job.customer?.phone || (seededDemo ? "543794751517" : ""),
          },
          notes: job.notes || (seededDemo ? "Tocar timbre al llegar." : ""),
          events: [
            ...(job.events || []),
            { status: "driver_assigned", createdAt: now },
          ],
        };
      });
      return { account: { ...account, status: "delivering" }, jobs: nextJobs };
    });
  }
  return invokeRemoteRider(
    { action: "claim_assignment", assignmentId },
    "No pudimos asignarte ese viaje."
  );
};

export const advanceWeRiderJob = async (assignmentId, nextStatus) => {
  if (useLocalPreview) {
    const dashboard = updateLocalDashboard(({ account, jobs }) => {
      const job = jobs.find((item) => item.id === assignmentId && item.assigned);
      if (!job) throw new Error("No encontramos ese viaje en tu cuenta.");
      const allowed = {
        driver_assigned: ["arriving_pickup"],
        arriving_pickup: ["arrived_pickup"],
        ready_for_pickup: ["arrived_pickup"],
        picked_up: ["in_transit"],
      }[job.status] || [];
      if (!allowed.includes(nextStatus)) {
        throw new Error("Ese paso todavía no corresponde en esta entrega.");
      }
      const now = new Date().toISOString();
      if (
        nextStatus === "arrived_pickup" &&
        job.pickupDeadlineAt &&
        new Date(job.pickupDeadlineAt).getTime() < Date.now()
      ) {
        throw new Error(
          "Se venció el tiempo para llegar. El restaurante debe reasignar el viaje."
        );
      }
      const nextJobs = jobs.map((item) =>
        item.id === assignmentId
          ? {
              ...item,
              status: nextStatus,
              pickupArrivedAt:
                nextStatus === "arrived_pickup"
                  ? now
                  : item.pickupArrivedAt || null,
              estimatedArrivalAt:
                nextStatus === "in_transit"
                  ? new Date(Date.now() + 18 * 60 * 1000).toISOString()
                  : item.estimatedArrivalAt || null,
              updatedAt: now,
              events: [...(item.events || []), { status: nextStatus, createdAt: now }],
            }
          : item
      );
      return {
        account: {
          ...account,
          status: "delivering",
        },
        jobs: nextJobs,
      };
    });
    if (nextStatus === "arriving_pickup") {
      window.setTimeout(() => {
        updateLocalDashboard(({ account, jobs }) => {
          const now = new Date().toISOString();
          return {
            account,
            jobs: jobs.map((item) =>
              item.id === assignmentId && item.status === "arriving_pickup"
                ? {
                    ...item,
                    status: "ready_for_pickup",
                    updatedAt: now,
                    events: [
                      ...(item.events || []),
                      { status: "ready_for_pickup", createdAt: now },
                    ],
                  }
                : item
            ),
          };
        });
      }, 1800);
    }
    return dashboard;
  }
  return invokeRemoteRider(
    { action: "advance_assignment", assignmentId, nextStatus },
    "No pudimos actualizar la entrega."
  );
};

export const verifyWeRiderPickupCode = async (assignmentId, code) => {
  const safeCode = String(code || "").replace(/\D/g, "").slice(0, 6);
  if (!/^\d{6}$/.test(safeCode)) {
    throw new Error("Ingresa los seis números que te muestra el restaurante.");
  }

  if (useLocalPreview) {
    return updateLocalDashboard(({ account, jobs }) => {
      const job = jobs.find((item) => item.id === assignmentId && item.assigned);
      if (!job) throw new Error("No encontramos ese viaje en tu cuenta.");
      if (job.status !== "arrived_pickup") {
        throw new Error("Primero confirma que llegaste al restaurante.");
      }
      if (
        !job.pickupDeadlineAt ||
        !job.pickupArrivedAt ||
        new Date(job.pickupArrivedAt).getTime() >
          new Date(job.pickupDeadlineAt).getTime()
      ) {
        throw new Error(
          "Se venció el tiempo de retiro. El restaurante debe reasignar el viaje."
        );
      }
      const currentAttempts = Number(job.pickupVerificationAttempts || 0);
      if (job.pickupVerificationLockedAt || currentAttempts >= 5) {
        throw new Error(
          "El código quedó bloqueado. Pide al restaurante que reasigne el viaje."
        );
      }
      if (safeCode !== job.pickupVerificationCode) {
        const nextAttempts = Math.min(5, currentAttempts + 1);
        const lockedAt = nextAttempts >= 5 ? new Date().toISOString() : null;
        const nextJobs = jobs.map((item) =>
          item.id === assignmentId
            ? {
                ...item,
                pickupVerificationAttempts: nextAttempts,
                pickupVerificationLockedAt: lockedAt,
                updatedAt: new Date().toISOString(),
              }
            : item
        );
        writeJson(LOCAL_JOBS_KEY, nextJobs);
        dispatchUpdated();
        throw new Error(
          nextAttempts >= 5
            ? "El código quedó bloqueado. Pide ayuda al restaurante."
            : "El código no coincide. Revisa los seis números con el restaurante."
        );
      }

      const now = new Date().toISOString();
      return {
        account: { ...account, status: "delivering" },
        jobs: jobs.map((item) =>
          item.id === assignmentId
            ? {
                ...item,
                status: "picked_up",
                pickedUpAt: now,
                pickupVerifiedAt: now,
                pickupVerificationCode: null,
                deliveryVerificationCode: createLocalDeliveryCode(),
                deliveryVerificationAttempts: 0,
                deliveryVerifiedAt: null,
                deliveryVerificationLockedAt: null,
                updatedAt: now,
                events: [
                  ...(item.events || []),
                  { status: "picked_up", createdAt: now },
                ],
              }
            : item
        ),
      };
    });
  }

  return invokeRemoteRider(
    { action: "verify_pickup_code", assignmentId, code: safeCode },
    "No pudimos verificar el retiro."
  );
};

export const verifyWeRiderDeliveryCode = async (assignmentId, code) => {
  const safeCode = String(code || "").replace(/\D/g, "").slice(0, 6);
  if (!/^\d{6}$/.test(safeCode)) {
    throw new Error("Ingresa los seis números que te muestra el cliente.");
  }

  if (useLocalPreview) {
    return updateLocalDashboard(({ account, jobs }) => {
      const job = jobs.find((item) => item.id === assignmentId && item.assigned);
      if (!job) throw new Error("No encontramos ese viaje en tu cuenta.");
      if (job.status !== "in_transit") {
        throw new Error("Primero inicia el viaje hacia el domicilio.");
      }
      const currentAttempts = Number(job.deliveryVerificationAttempts || 0);
      if (job.deliveryVerificationLockedAt || currentAttempts >= 5) {
        throw new Error(
          "El código quedó bloqueado. Comunícate con soporte antes de entregar."
        );
      }
      if (!job.deliveryVerificationCode) {
        throw new Error("El cliente todavía no tiene un código de entrega disponible.");
      }
      if (safeCode !== job.deliveryVerificationCode) {
        const nextAttempts = Math.min(5, currentAttempts + 1);
        const lockedAt = nextAttempts >= 5 ? new Date().toISOString() : null;
        const nextJobs = jobs.map((item) =>
          item.id === assignmentId
            ? {
                ...item,
                deliveryVerificationAttempts: nextAttempts,
                deliveryVerificationLockedAt: lockedAt,
                updatedAt: new Date().toISOString(),
              }
            : item
        );
        writeJson(LOCAL_JOBS_KEY, nextJobs);
        dispatchUpdated();
        throw new Error(
          nextAttempts >= 5
            ? "El código quedó bloqueado. Comunícate con soporte antes de entregar."
            : "El código no coincide. Revísalo con el cliente."
        );
      }

      const now = new Date().toISOString();
      return {
        account: { ...account, status: "available" },
        jobs: jobs.map((item) =>
          item.id === assignmentId
            ? {
                ...item,
                status: "delivered",
                deliveredAt: now,
                deliveryVerifiedAt: now,
                deliveryVerificationCode: null,
                updatedAt: now,
                events: [
                  ...(item.events || []),
                  { status: "delivered", createdAt: now },
                ],
              }
            : item
        ),
      };
    });
  }

  return invokeRemoteRider(
    { action: "verify_delivery_code", assignmentId, code: safeCode },
    "No pudimos confirmar la entrega."
  );
};

export const updateWeRiderLocation = async ({ latitude, longitude }) => {
  if (!Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) {
    throw new Error("La ubicación no es válida.");
  }
  if (useLocalPreview) {
    updateLocalDashboard(({ account, jobs }) => ({
      account: {
        ...account,
        location: {
          latitude: Number(latitude),
          longitude: Number(longitude),
          updatedAt: new Date().toISOString(),
        },
      },
      jobs,
    }));
    return { ok: true };
  }
  return invokeRemoteRider(
    { action: "update_location", latitude, longitude },
    "No pudimos actualizar tu ubicación."
  );
};

export const resetLocalWeRiderDemo = () => {
  if (!useLocalPreview) return;
  getStorage()?.removeItem(LOCAL_ACCOUNT_KEY);
  getStorage()?.removeItem(LOCAL_SESSION_KEY);
  getStorage()?.removeItem(LOCAL_JOBS_KEY);
  clearLocalWeRiderIdentityDocuments().catch(() => null);
  dispatchUpdated();
};
